/** GOA 2026 conference campaign — frontend POC constants */

export const GOA_2026_CAMPAIGN_SLUG = "goa_2026";

export const GOA_2026_SESSION_BEST_KEY = "goa_2026_session_best";

/** Hashtag for auto-posts and leaderboard feed */
export const GOA_2026_CAMPAIGN_HASHTAG = "GOASightIn";

export const GOA_2026_SHOT_COUNT = 5;

/** Visible round countdown from first shot (ms). */
export const GOA_2026_ROUND_TIME_LIMIT_MS = 60_000;

/** Pause after last shot before the run recap modal (ms). */
export const GOA_2026_RUN_RESULTS_DELAY_MS = 750;

/** Hearts (lives) tiers from invites sent before first run — highest matching tier wins. */
export const GOA_2026_LIVES_TIERS = [
  { minInvitesSent: 20, hearts: 7 },
  { minInvitesSent: 10, hearts: 5 },
  { minInvitesSent: 0, hearts: 3 },
] as const;

export function goa2026HeartsForInvitesSent(invitesSent: number): number {
  const tier = GOA_2026_LIVES_TIERS.find((t) => invitesSent >= t.minInvitesSent);
  return tier?.hearts ?? 3;
}

/** Maximum hearts unlockable via onboarding invites (7♥ tier). */
export const GOA_2026_MAX_LIVES = GOA_2026_LIVES_TIERS[0].hearts;

/** Game-over modal can only raise livesMax up to this cap. */
export function canGameOverInvitesUnlockHearts(livesMax: number): boolean {
  return livesMax < GOA_2026_MAX_LIVES;
}

/** Next heart tier reachable via game-over invites alone (respects current livesMax). */
export function goa2026GameOverNextHeartTier(
  livesMax: number,
  gameOverInvitesSent: number,
): { needed: number; nextHearts: number } | null {
  if (livesMax >= GOA_2026_MAX_LIVES) return null;

  if (livesMax < 5 && gameOverInvitesSent < 10) {
    return { needed: 10 - gameOverInvitesSent, nextHearts: 5 };
  }

  if (livesMax < 7 && gameOverInvitesSent < 20) {
    return { needed: 20 - gameOverInvitesSent, nextHearts: 7 };
  }

  return null;
}

/** Invite multiplier boosts (see campaign plan). */
export const GOA_2026_INVITE_SENT_BOOST = 0.02;
export const GOA_2026_INVITE_JOINED_BOOST = 0.06;
export const GOA_2026_MAX_INVITE_MULTIPLIER = 2.0;

/** Arcade shot points per ring (outer → inner) — v2 scoring. */
export const GOA_2026_ARCADE_RING_POINTS = [2000, 4000, 6000, 8000, 10000] as const;

/**
 * Resolve a public/ asset against the build's base URL. In prod the base is the
 * CDN (VITE_CDN_BASE_URL); a literal "/campaign/..." string bypasses Vite's asset
 * rewriting and resolves against the app host, which has no static files and
 * serves the SPA index.html instead (broken images). import.meta.env.BASE_URL is
 * "/" in dev and the CDN base (with trailing slash) in prod.
 */
function goa2026Asset(relativePath: string): string {
  return `${import.meta.env.BASE_URL}${relativePath}`.replace(/([^:]\/)\/+/g, "$1");
}

/** Single sight-in bullseye sheet (Gunclub logo center). */
export const GOA_2026_PRINT_SVG_URL = goa2026Asset(
  "campaign/goa2026/goa-sight-in-print-target.svg",
);

/** 8-bit pixel heart sprite for lives HUD */
export const GOA_2026_PIXEL_HEART_URL = goa2026Asset("campaign/goa2026/pixel-heart.svg");

/** 8-bit pixel bullet sprite for shots HUD */
export const GOA_2026_PIXEL_BULLET_URL = goa2026Asset("campaign/goa2026/pixel-bullet.svg");

/** Bolt-action chamber sound — first 1s of clip used on reload. */
export const GOA_2026_RELOAD_SOUND_URL = goa2026Asset("campaign/goa2026/bolt-action-reload.wav");

export const GOA_2026_RELOAD_DURATION_SEC = 1;

/** Rifle shot on fire. */
export const GOA_2026_FIRE_SOUND_URL = goa2026Asset("campaign/goa2026/rifle-shot.wav");

export const GOA_2026_PRINT_URL = "/goa/2026/print";
export const GOA_2026_PLAY_URL = "/goa/2026/play";
export const GOA_2026_INVITE_URL = "/goa/2026/invite";
export const GOA_2026_RESULTS_URL = "/goa/2026/results";
export const GOA_2026_LEADERBOARD_URL = "/goa/2026/leaderboard";

/** @deprecated use GOA_2026_PRINT_SVG_URL */
export const GOA_2026_PRINT_TARGET_SVG_URL = GOA_2026_PRINT_SVG_URL;

/** @deprecated use GOA_2026_PRINT_URL */
export const GOA_2026_PRINT_TARGET_URL = GOA_2026_PRINT_URL;

/** Normalized ring radii (fraction of half-size) — outer to inner scoring zones */
export const GOA_2026_RING_RADII = [0.4, 0.32, 0.24, 0.16, 0.08] as const;

/** Points per ring index (outer ring = lowest score) */
export const GOA_2026_RING_POINTS = [20, 40, 60, 80, 100] as const;

/** Consecutive tracked frames required before starting play. */
export const GOA_2026_MARKER_LOCK_FRAMES = 6;

/** Minimum tracker confidence (0–1) to lock or fire. */
export const GOA_2026_MARKER_MIN_CONFIDENCE = 0.42;

export function goa2026PrintTargetPath(): string {
  return GOA_2026_PRINT_URL;
}

export function goa2026PrintTargetSvgUrl(): string {
  return GOA_2026_PRINT_SVG_URL;
}

export function goa2026PlayPath(): string {
  return GOA_2026_PLAY_URL;
}

export function goa2026LeaderboardPath(): string {
  return GOA_2026_LEADERBOARD_URL;
}

/** @deprecated hashtag search — use goa2026LeaderboardPath */
export function goa2026LeaderboardSearchPath(): string {
  return goa2026LeaderboardPath();
}

export function goa2026CampaignBasePath(): string {
  return "/goa/2026";
}

/** Enable campaign routes and entry points (see `.env.example`) */
export const isGoa2026CampaignEnabled =
  import.meta.env.VITE_ENABLE_GOA_2026_CAMPAIGN === "true" ||
  import.meta.env.VITE_ENABLE_GOA_2026_CAMPAIGN === "1";
